import angular, { IPromise } from 'angular'
import find from 'lodash/find'
import filter from 'lodash/filter'
import sortBy from 'lodash/sortBy'
import findIndex from 'lodash/findIndex'
import merge from 'lodash/merge'
import each from 'lodash/each'
import isEmpty from 'lodash/isEmpty'
import omit from 'lodash/omit'
import { vn, subtract, round, add } from 'vend-number'
import { ApiRegister } from '@vend/types'
import { PAYMENT_TYPES } from '@vend/business-components'

import { getFromStorage, saveToStorage, trackUserEvent } from '@vend/utilities'
import { ToastNotifications } from '@lightspeed/design-system-react'
import {
  CASH_MOVEMENT_TYPES,
  FEATURE_ENABLE_REGISTER_ACCESS_CONTROL,
  PERMISSIONS,
} from '../constants'
import { isCashConcealedPaymentType } from '../functions/payments'
import { WEB_REGISTER } from '../components/web-register.constant'
import { registersStore } from '../factories/database/registers-store'
import { paymentTypesStore } from '../factories/database/payment-types-store'
import { settingsStore } from '../factories/database/settings-store'
import {
  ClosurePayment,
  RegisterClosure,
  RegisterClosurePrintData,
} from '../util/register-closure-types'
import { getFeatureFromCache } from '../util/feature-cache'
import { registerManager } from '../ng-compat/managers'
import { registersResource } from '../components/register/registers-resource/registers-resource'
import { cashMovementsManager } from './cash-movements-manager'
import { getPaymentManager } from './payment-manager'
import { userManager } from './user-manager'

const NOT_APPLICABLE = '-'
const DECIMAL_PLACES = 2
const SYSTEM_PAYMENT_TYPES = [PAYMENT_TYPES.XERO]
const LATEST_REGISTER_CLOSURE_KEY = 'LATEST_REGISTER_CLOSURE'
const REGISTER_OPEN_SEQUENCE = 'VEND_REGISTER_OPEN_SEQUENCE'

export class RegisterClosureManager {
  private _timeInitialized: Date | null
  private _timeClosed: Date | null

  private userManager = userManager
  private paymentManager = getPaymentManager()
  private cashMovementsManager = cashMovementsManager
  private registersResource = registersResource

  constructor() {
    this._timeInitialized = null
    this._timeClosed = null
  }

  /**
   * Get the current register closure summary of the active, open register.
   *
   * @method getRegisterClosureSummary
   *
   * @return {Promise<RegisterClosure>} A promise resolved with the register closure details of the active,
   *         open register
   */
  public async getRegisterClosureSummary(): Promise<RegisterClosure> {
    const registerClosurePromise = this._getSavedRegisterClosure()
    const paymentsSummaryPromise = this.registersResource.getPaymentsSummary(
      registerManager.getActiveRegister()
    )

    const cashMovementsEnabled = this.cashMovementsManager.isCashManagementEnabled()
    const cashDrawerTotalsPromise = cashMovementsEnabled
      ? this.cashMovementsManager.getCashDrawerTotals()
      : Promise.resolve(null)
    const cashMovementsPromise = cashMovementsEnabled
      ? this.cashMovementsManager.getCashMovements()
      : Promise.resolve(null)

    const [
      registerClosure,
      paymentsSummary,
      cashDrawerTotals,
      cashMovements,
    ] = await Promise.all([
      registerClosurePromise,
      paymentsSummaryPromise,
      cashDrawerTotalsPromise,
      cashMovementsPromise,
    ])

    const data = cashMovementsEnabled
      ? { registerClosure, paymentsSummary, cashDrawerTotals, cashMovements }
      : { registerClosure, paymentsSummary }

    const registerClosureWithPaymentInfo = await this._updatePaymentsSummaryWithPaymentTypeInfo(
      data
    )
    return this._updateRegisterClosureWithPaymentsSummary(
      registerClosureWithPaymentInfo
    )
  }

  /**
   * Save the given register closure to IndexedDB.
   *
   * @method saveRegisterClosure
   *
   * @param  registerClosure {RegisterClosure}
   *         The register closure to save
   *
   * @return {Promise<RegisterClosure>} A promise resolved with the given register closure once it has been
   *         saved
   */
  public saveRegisterClosure(registerClosure: $TSFixMe) {
    return settingsStore
      .put(LATEST_REGISTER_CLOSURE_KEY, registerClosure)
      .then(() => registerClosure)
  }

  /**
   * Get the last register closure summary for the current active register.
   *
   * @method getLastRegisterClosureSummary
   *
   * @return {Promise<RegisterClosure>} A promise resolved with the register closure or resolved with null if
   *         there is no applicable register closure
   */
  public getLastRegisterClosureSummary() {
    return this._getSavedRegisterClosure()
  }

  /**
   * Open the given register. Will prompt for a cash float if cash management is enabled.
   *
   * @method openRegister
   *
   * @param  register {Register}
   *         The register to open.
   *
   * @param  {Object} openingCashFloat
   *         The cash movement object for the opening cash float.
   *
   * @return {Promise<Register>} A promise resolved with the updated, opened register once the given register
   *         has been opened
   */
  public openRegister(
    register: ApiRegister,
    openingCashFloat: any
  ): IPromise<unknown> {
    if (this.cashMovementsManager.isCashManagementEnabled()) {
      return this.openRegisterAndSetFloat(register, openingCashFloat).catch(
        (error: $TSFixMe) => {
          if (!registerManager.isActiveRegisterOpen()) {
            return Promise.reject(error)
          }
          ToastNotifications.negative(
            `We had a problem saving the opening cash drawer amount. To retry, open Cash Management in the side menu bar.`
          )
        }
      )
    } else {
      return this.registersResource
        .open(register)
        .then((openedRegister: $TSFixMe) => {
          this._saveRegisterSequenceId(
            openedRegister.id,
            openedRegister.register_open_sequence_id
          )
          return this._updateRegisterAfterOpen(openedRegister)
        })
        .catch(() => this._onRegisterOpenErrorNotify())
    }
  }

  /**
   * Open the given register and set an opening cash float.
   *
   * @method openRegisterAndSetFloat
   *
   * @param  {Register} register
   *         The register to open.
   *
   * @param  {Object} openingCashFloat
   *         The cash movement object for the opening cash float.
   *
   * @return {Promise<Register>} A promise resolved with the updated register once it has been opened and the
   *         opening cash float has been set, and if cash management is enabled, the opening cash float
   */
  public openRegisterAndSetFloat(register: ApiRegister, openingCashFloat: any) {
    let setCashFloatFailed = false
    let cashMovement: $TSFixMe
    return this.registersResource
      .open(register)
      .then(register => {
        return new Promise<void>((resolve, reject) => {
          if (!this.cashMovementsManager.isCashManagementEnabled()) {
            // eslint-disable-next-line prefer-promise-reject-errors
            return reject()
          }
          return (
            this.cashMovementsManager
              .recordCashMovement(openingCashFloat, register)
              .then((openingFloatMovement: $TSFixMe) => {
                cashMovement = openingFloatMovement
                return resolve()
              })
              // eslint-disable-next-line prefer-promise-reject-errors
              .catch(() => reject())
          )
        })
          .then(() => register)
          .catch(() => {
            setCashFloatFailed = true
            return register
          })
      })
      .then(register => {
        this._saveRegisterSequenceId(
          register.id,
          register.register_open_sequence_id!
        )
        return this._updateRegisterAfterOpen(register)
      })
      .catch(() => this._onRegisterOpenErrorNotify())
      .then(register => {
        if (setCashFloatFailed) {
          return Promise.reject(new Error())
        }
        return { register, cashMovement }
      })
  }

  /**
   * Close the associated register with the given register closure. If cash management is enabled, we also
   * determine any required cash movements to be registered.
   *
   * @method closeRegister
   *
   * @param  registerClosure {RegisterClosure}
   *         The register closure information to post to the API to close the register
   *
   * @return {Promise<Register>} A promise resolved with the updated, closed register once the given register
   *         closure has been posted and the associated register closed
   */
  public closeRegister(registerClosure: $TSFixMe) {
    this._trackRegisterClosureTime()

    if (this.cashMovementsManager.isCashManagementEnabled()) {
      return this._recordClosingCashMovementsForRegister(
        registerClosure
      ).then(() => this._doCloseRegister(registerClosure))
    } else {
      return this._doCloseRegister(registerClosure)
    }
  }

  /**
   * Calculate the difference between the expected total and the counted total.
   *
   * @method differenceBetweenExpectedAndCounted
   *
   * @param  total {VendNumber}
   *         The expected total for this payment type
   *
   * @param  totalCounted {VendNumber}
   *         The actual counted total for this payment type
   *
   * @return {VendNumber|String} The difference between the two totals or "–" if there is no actual count
   *         yet
   */
  public differenceBetweenExpectedAndCounted(
    total: string | number,
    totalCounted: string | number
  ) {
    let difference
    if (isEmpty(totalCounted)) {
      difference = NOT_APPLICABLE
    } else {
      difference = subtract(round(totalCounted, 2), round(total, 2))
      difference = round(difference, DECIMAL_PLACES)
    }
    return difference
  }

  /**
   * Determine the expected value. If cash concealed "-" is returned. When cash management is enabled and we're
   * dealing with a cash management payment type then we'll return the cash drawer balance. If none of these match
   * we'll return the payment total.
   *
   * @method determineExpectedPaymentTotal
   *
   * @param {Object} payment
   *
   * @param {Object} registerClosure
   *
   * @return {Number|String} Either a number representing the expected value or a placeholder incase of cash
   * concealed payment type
   */
  public determineExpectedPaymentTotal(
    payment: ClosurePayment,
    registerClosure: RegisterClosure
  ) {
    // We need to check hasCashDrawerBalance here to cater for the edge case where if the user enabled cash management
    // while on the open register page (Viewing the previous closure) and hits refresh.
    const hasCashDrawerBalance = Boolean(
      registerClosure.cashDrawerTotals &&
        registerClosure.cashDrawerTotals.cashDrawerBalance !== undefined
    )
    if (isCashConcealedPaymentType(payment)) {
      return NOT_APPLICABLE
    }
    if (
      this.cashMovementsManager.isCashManagementEnabled() &&
      this.paymentManager.isCashManagementPaymentType(payment) &&
      hasCashDrawerBalance
    ) {
      return round(
        registerClosure.cashDrawerTotals.cashDrawerBalance,
        DECIMAL_PLACES
      )
    }
    return round(payment.total, DECIMAL_PLACES)
  }

  /**
   * Determine the register closure summary totals
   *
   * @method determineSummaryTotals
   *
   * @param {Object} registerClosure
   *
   * @param {Boolean} hasCashConcealedPaymentType
   *
   * @returns {Object} The register closure totals
   */
  public determineSummaryTotals(
    registerClosure: any,
    hasCashConcealedPaymentType: boolean
  ) {
    const totals = {
      expectedTotal: '0.00',
      countedTotal: '0.00',
      differenceTotal: '0.00',
      countedValid: true,
    }
    if (hasCashConcealedPaymentType) {
      totals.expectedTotal = NOT_APPLICABLE
      totals.differenceTotal = NOT_APPLICABLE
    }
    each(registerClosure.payments, payment => {
      // Check if the counted value is still valid. If not we'll render "-" in the counted section
      if (totals.countedValid) {
        // Determine the counted value for the cash management payment type
        if (
          this.paymentManager.isCashManagementPaymentType(payment) &&
          registerClosure.countedCashInDrawer
        ) {
          totals.countedTotal = round(
            add(totals.countedTotal, registerClosure.countedCashInDrawer),
            2
          )
          // Determine the counted value for non cash management types
        } else if (
          !this.paymentManager.isCashManagementPaymentType(payment) &&
          payment.totalCounted
        ) {
          totals.countedTotal = round(
            add(totals.countedTotal, payment.totalCounted),
            2
          )
        } else {
          totals.countedTotal = NOT_APPLICABLE
          totals.countedValid = false
        }
      }
      if (!hasCashConcealedPaymentType) {
        const paymentTotal = this.determineExpectedPaymentTotal(
          payment,
          registerClosure
        )
        totals.expectedTotal = round(add(totals.expectedTotal, paymentTotal), 2)
        // Check if the counted value is still valid. If not we'll render "-" in the difference section
        if (totals.countedValid) {
          totals.differenceTotal = this.differenceBetweenExpectedAndCounted(
            totals.expectedTotal,
            totals.countedTotal
          )
        } else {
          totals.differenceTotal = NOT_APPLICABLE
        }
      }
    })
    return totals
  }

  /**
   * Determine the cash to bank to display on the summary view. Calculated as counted cash in drawer minus the
   * the opening float. If the difference is a negative amount, then cash to bank is zero. Also check's that
   * openingCashFloat and countedCashInDrawer are available - handling the instance where a retailer closes a
   * register with cashman disabled, enables cashman and then refreshes the register closure summary page.
   *
   * @method calculateCashToBank
   *
   * @param  {Number} openingCashFloat
   *         The opening cash float amount
   *
   * @param  {String} countedCashInDrawer
   *         The counted cash in drawer
   *
   * @return {String} cashToBank
   *         The calculated cash to bank
   */
  public calculateCashToBank(
    openingCashFloat: number,
    countedCashInDrawer: string
  ) {
    let cashToBank = '0.00'
    if (
      typeof openingCashFloat !== 'undefined' &&
      typeof countedCashInDrawer !== 'undefined'
    ) {
      cashToBank = this.differenceBetweenExpectedAndCounted(
        openingCashFloat,
        countedCashInDrawer
      )
      if (vn(cashToBank).isNeg()) {
        cashToBank = '0.00'
      }
    }
    return cashToBank
  }

  /**
   * Determine the closing float, will check that openingCashFloat & countedCashInDrawer are defined, otherwise
   * will return 0.00. Handling the instance where a retailer closes a register with cashman disabled,
   * enables cashman and then refreshes the register closure summary page.
   *
   * @method calculateClosingFloat
   *
   * @param  {Number} openingCashFloat
   *         The opening cash float amount
   *
   * @param  {String} countedCashInDrawer
   *         The counted cash in drawer
   *
   * @return {String} closingFloat
   *         The calculated closing float
   */
  public calculateClosingFloat(
    openingCashFloat: number,
    countedCashInDrawer: string
  ) {
    let closingFloat = '0.00'
    if (
      typeof openingCashFloat !== 'undefined' &&
      typeof countedCashInDrawer !== 'undefined'
    ) {
      const cashToBank = this.calculateCashToBank(
        openingCashFloat,
        countedCashInDrawer
      )
      closingFloat = round(
        subtract(countedCashInDrawer, cashToBank),
        DECIMAL_PLACES
      )
    }
    return closingFloat
  }

  /**
   * Generate the register closure print data
   *
   * @method generateRegisterClosurePrintData
   *
   * @param {Object} registerClosure
   *
   * @return {Object} An object representing the register closure data
   */
  public generateRegisterClosurePrintData(
    registerClosure: RegisterClosure
  ): RegisterClosurePrintData {
    const data: $TSFixMe = {
      registerClosure,
      isCashManagementEnabled: this.cashMovementsManager.isCashManagementEnabled(),
      hasCashConcealedPaymentType: false,
      summaryTotals: {},
    }
    if (data.isCashManagementEnabled) {
      const cashManagementCashFloat = find(data.registerClosure.cashMovements, {
        type: CASH_MOVEMENT_TYPES.OPENING_CASH_FLOAT,
      })
      data.cashManagement = {
        isCashFloatSet: Boolean(cashManagementCashFloat),
        closingFloat: this.calculateClosingFloat(
          registerClosure.openingCashFloat,
          registerClosure.countedCashInDrawer
        ),
        cashToBank: this.calculateCashToBank(
          registerClosure.openingCashFloat,
          registerClosure.countedCashInDrawer
        ),
      }
    }
    data.registerClosure.payments.forEach((payment: $TSFixMe) => {
      if (isCashConcealedPaymentType(payment)) {
        data.hasCashConcealedPaymentType = true
      }

      if (data.isCashManagementEnabled) {
        payment.isCashManagementPaymentType = this.paymentManager.isCashManagementPaymentType(
          payment
        )
      }
      // Here the value of countedPaymentTotal is based on whether cash management is the payment type
      if (data.isCashManagementEnabled && payment.isCashManagementPaymentType) {
        payment.countedPaymentTotal = data.registerClosure.countedCashInDrawer
      } else {
        payment.countedPaymentTotal = payment.totalCounted
      }
      payment.expectedPaymentTotal = this.determineExpectedPaymentTotal(
        payment,
        data.registerClosure
      )
    })
    data.summaryTotals = this.determineSummaryTotals(
      data.registerClosure,
      data.hasCashConcealedPaymentType
    )
    return data
  }

  /**
   * Check whether current user has the permission to open or close a register
   *
   * @method hasOpenClosePermission
   */
  public hasOpenClosePermission(): boolean {
    if (getFeatureFromCache(FEATURE_ENABLE_REGISTER_ACCESS_CONTROL)) {
      return true
    }

    return (
      this.userManager.hasPermission(PERMISSIONS.REGISTER_OPEN_CLOSE) &&
      this.userManager.hasPermission(PERMISSIONS.ACCESS)
    )
  }

  // ============================== Private ============================== //
  /**
   * Close the register and sync with the registers store.
   *
   * @private
   * @method _doCloseRegister
   *
   * @param  registerClosure {RegisterClosure}
   *         The register closure information to post to the API to close the register
   *
   * @return {Promise<Register>} A promise resolved with the updated, closed register.
   */
  private _doCloseRegister(registerClosure: $TSFixMe) {
    return this.registersResource
      .close(registerClosure)
      .then((closedRegister: $TSFixMe) => {
        registerManager.clearRegisterOpenReminderSeen({
          registerId: closedRegister.id,
        })
        this._removeRegisterSequenceId(closedRegister.id)
        return this._syncUpdatedRegister(closedRegister)
      })
      .then((updatedRegister: $TSFixMe) =>
        this._updateAndSaveRegisterClosure(registerClosure, updatedRegister)
      )
      .catch(() => this._onRegisterCloseErrorNotify())
  }

  /**
   * Updates the provided register with the IndexedDB store after opening.
   *
   * @private
   * @method _updateRegisterAfterOpen
   *
   * @param  {Register} openedRegister
   *         The recently opened register.
   *
   * @return {Promise<Register>} A promise resolved with the updated register.
   *
   */
  private _updateRegisterAfterOpen(openedRegister: ApiRegister) {
    return this._syncUpdatedRegister(openedRegister)
  }

  /**
   * Determine's the closing float for the register and any potential discrepancies.
   *
   * If the counted cash in drawer is less than the opening float, then adjust the closing float and record as a
   * float adjustment.
   *
   * If the counted cash in till does not equal the expected cash in till then record the difference as a  till
   * discrepancy.
   *
   * @private
   * @method _recordClosingCashMovementsForRegister
   *
   * @param {RegisterClosure} registerClosure
   *        The register closure object returned from the register closure summary table
   *
   * @return {Promise} Promises resolved when the closing cash and discrepancy adjustments have been made.
   */
  private _recordClosingCashMovementsForRegister(registerClosure: $TSFixMe) {
    // LR-8075: `countedCashInDrawer` will be undefined if it is not defaulted
    // to 0 which causes the math operations to fail and affecting the closure
    const { countedCashInDrawer = '0.00', openingCashFloat } = registerClosure
    const { cashDrawerBalance } = registerClosure.cashDrawerTotals
    const closingCashMovementsDeferred = []
    let closingCashFloat = -Math.abs(vn(openingCashFloat).toNumber())
    // Determine if the counted cash in drawer is less than the opening float
    if (vn(countedCashInDrawer).lt(openingCashFloat)) {
      let floatAdjustmentAmount = subtract(
        countedCashInDrawer,
        openingCashFloat
      )
      floatAdjustmentAmount = -Math.abs(
        vn(round(floatAdjustmentAmount, 2)).toNumber()
      )
      // Change the closing cash float to be the counted cash in drawer
      closingCashFloat = -Math.abs(vn(round(countedCashInDrawer, 2)).toNumber())
      // Register a float adjustment
      closingCashMovementsDeferred.push(
        this.cashMovementsManager.recordFloatAdjustment(floatAdjustmentAmount)
      )
    }
    // Determine any differences between counted cash in drawer and expected cash in drawer
    if (vn(countedCashInDrawer).cmp(cashDrawerBalance) !== 0) {
      const drawerDifference = round(
        subtract(countedCashInDrawer, cashDrawerBalance),
        2
      )
      closingCashMovementsDeferred.push(
        this.cashMovementsManager.recordTillDiscrepancy(drawerDifference)
      )
    }
    closingCashMovementsDeferred.push(
      this.cashMovementsManager.recordClosingFloat(closingCashFloat)
    )
    // Catch errors when performing any of the closing cash management requests, while still allowing the register
    // to close.
    return Promise.all(closingCashMovementsDeferred).catch(error => {
      trackUserEvent(
        WEB_REGISTER.DISPLAY_NAME,
        'Failed Cash Management Register Closure',
        { error }
      )
    })
  }

  /**
   * Sync an updated register object with the registers IndexedDB store.
   *
   * @private
   * @method _syncUpdatedRegister
   *
   * @param  updatedRegister {Register}
   *         The register that was updated (likely after opening/closing)
   *
   * @return {Promise<Register>} A promise resolved with the given register once it has been synced with the
   *         database
   */
  private _syncUpdatedRegister(updatedRegister: ApiRegister) {
    return registersStore
      .syncData([updatedRegister])
      .then(() => updatedRegister)
  }

  /**
   * Triggers an error notification if an error occurs trying to open a register.
   *
   * @private
   * @method _onRegisterOpenErrorNotify
   *
   * @param  {Object} errorDetails
   *         The error details from the API response
   *
   * @return {Object} The error details from the API response
   */
  private _onRegisterOpenErrorNotify() {
    ToastNotifications.negative('Register failed to open, please try again.')
    return Promise.reject(new Error())
  }

  /**
   * Triggers an error notification if an error occurs trying to close a register.
   *
   * @private
   * @method _onRegisterCloseErrorNotify
   *
   * @param  {Object} errorDetails
   *         The error details from the API response
   *
   * @return {Object} The error details from the API response
   */
  private _onRegisterCloseErrorNotify() {
    ToastNotifications.negative('Register failed to close, please try again.')
    return Promise.reject(new Error())
  }

  /**
   * Update every payments with their payment type `type_id`.
   *
   * @private
   * @method _updatePaymentsSummaryWithPaymentTypeId
   *
   * @param  {Object} data
   *
   * @return {Promise} Resolves with the updated register closure
   */
  private _updatePaymentsSummaryWithPaymentTypeInfo(data: any) {
    return paymentTypesStore.all().then(paymentTypes => {
      data.paymentsSummary.payments.forEach(function (payment: $TSFixMe) {
        payment.paymentType = find(paymentTypes, {
          id: payment.payment_type_id,
        })
        // System payment types are not returned as part of the payment type store, so will be undefined.
        // We also check specific payment type id, just in case
        payment.isSystemPaymentType =
          typeof payment.paymentType === 'undefined' ||
          SYSTEM_PAYMENT_TYPES.indexOf(payment.paymentType.type_id) !== -1
      })
      return data
    })
  }

  /**
   * Update the given register closure with the given payments summary data from the API.
   *
   * @private
   * @method _updateRegisterClosureWithPaymentsSummary
   *
   * @param  {Object} data
   *
   *         @param data.paymentsSummary {PaymentsSummary}
   *                The payments summary data from the API
   *
   *         @param [data.cashDrawerTotals] {CashDrawerTotals}
   *                Current cash drawer totals for cash management
   *
   *         @param [data.cashMovements] {cashMovements}
   *                Current cash movements for cash management
   *
   *         @param [data.registerClosure] {RegisterClosure}
   *                The register closure to update (a new one is created if none supplied)
   *
   * @return {RegisterClosure} The updated register closure
   */
  private _updateRegisterClosureWithPaymentsSummary(data: {
    paymentsSummary: $TSFixMe
    cashDrawerTotals?: $TSFixMe
    cashMovements?: $TSFixMe
    registerClosure?: $TSFixMe
  }) {
    let registerClosure = data.registerClosure
    const paymentsSummary = data.paymentsSummary
    const closureSummarySequenceNumber =
      registerClosure && registerClosure.register_closure_sequence_number
    if (
      closureSummarySequenceNumber !==
      paymentsSummary.register_closure_sequence_number
    ) {
      registerClosure = {
        register: registerManager.getActiveRegister(),
        outlet: registerManager.getActiveOutlet(),
      }
    }
    // Cash drawer totals and cash movements for cash management
    registerClosure.cashDrawerTotals = data.cashDrawerTotals
    registerClosure.cashMovements = data.cashMovements
    // Check if register payments are available in registerClosure.payments & paymentsSummary.payments, these could
    // be missing on fresh load or reset data
    if (registerClosure.payments) {
      registerClosure.payments = this._filterAndOrderRegisterClosurePaymentTypes(
        registerClosure.payments
      )
    }
    if (paymentsSummary && paymentsSummary.payments) {
      paymentsSummary.payments = this._filterAndOrderRegisterClosurePaymentTypes(
        paymentsSummary.payments
      )
    }
    merge(registerClosure, paymentsSummary)
    return registerClosure
  }

  /**
   * Update the given register closure with the supplied register details and save the register closure to
   * IndexedDB.
   *
   * @private
   * @method _updateAndSaveRegisterClosure
   *
   * @param  registerClosure {RegisterClosure}
   *         The register closure to update
   *
   * @param  updatedRegister {Register}
   *         The updated register details to apply to the register closure
   *
   * @return {Promise<Register>} A promise resolved with the updated register details once the register
   *         closure has been updated and saved
   */
  private _updateAndSaveRegisterClosure(
    registerClosure: $TSFixMe,
    updatedRegister: ApiRegister
  ) {
    registerClosure.register = updatedRegister
    registerClosure.register_close_time = updatedRegister.register_close_time
    return this.saveRegisterClosure(registerClosure).then(() => updatedRegister)
  }

  /**
   * Get the last saved register closure from IndexedDB.
   *
   * @private
   * @method _getSavedRegisterClosure
   *
   * @return {Promise<RegisterClosure>} A promise resolved with the last saved register closure or `null` if
   *         either there is none or it is not applicable to the currently active register
   */
  private _getSavedRegisterClosure() {
    return settingsStore
      .get(LATEST_REGISTER_CLOSURE_KEY)
      .then((registerClosure: RegisterClosure | undefined) =>
        this._validateAndUpdateSavedRegisterClosure(registerClosure)
      )
  }

  /**
   * Filter and order the payments array
   * If cash management is enabled then ensure the cash management payment type is listed first
   *
   * @private
   *
   * @method _filterAndOrderRegisterClosurePaymentTypes
   *
   * @param {Array} A payments array used to identify payment types
   *
   * @return {Array} The payments array
   */
  private _filterAndOrderRegisterClosurePaymentTypes(payments: $TSFixMe) {
    if (!payments) {
      return
    }
    // Remove all non system payment types from the array
    payments = filter(payments, payment => !payment.isSystemPaymentType)
    payments = sortBy(payments, payment => payment.payment_type_name)
    // When cash management is eanbled, move the cash management payment type to the first position in the array
    if (this.cashMovementsManager.isCashManagementEnabled()) {
      const cashMangementPaymentTypeIndex = findIndex(payments, payment => {
        return this.paymentManager.isCashManagementPaymentType(payment)
      })
      const cashManagementPaymentType = payments.splice(
        cashMangementPaymentTypeIndex,
        1
      )[0]
      payments.unshift(cashManagementPaymentType)
    }
    return payments
  }

  /**
   * Validate that the given register closure is applicable to the currently active register and if so update
   * the register and outlet references to the current versions.
   *
   * @private
   * @method _validateAndUpdateSavedRegisterClosure
   *
   * @param  registerClosure {RegisterClosure}
   *         The register closure to validate and update
   *
   * @return {RegisterClosure} The updated register closure or null if it is not valid
   */
  private _validateAndUpdateSavedRegisterClosure(
    registerClosure?: RegisterClosure
  ) {
    const activeRegister = registerManager.getActiveRegister()
    const activeRegisterId = activeRegister && activeRegister.id
    if (!registerClosure || registerClosure.register.id !== activeRegisterId) {
      return null
    } else {
      // Update the register and outlet in case any details have changed since this was saved
      registerClosure.register = activeRegister
      registerClosure.outlet = registerManager.getActiveOutlet()
    }
    return registerClosure
  }

  /**
   * Save the active register open sequence ID for use in the 'Register open sequence ID mismatch' UBE
   *
   * @private
   * @method _saveRegisterSequenceId
   *
   * @param registerId {String}
   *        The ID of the opened register
   *
   * @param registerOpenSequenceId {String}
   *        The register open sequence ID of the opened register
   */
  private _saveRegisterSequenceId(
    registerId: string,
    registerOpenSequenceId: string
  ) {
    const data =
      getFromStorage<Record<string, string | null>>(REGISTER_OPEN_SEQUENCE) ||
      {}
    data[registerId] = registerOpenSequenceId
    saveToStorage(REGISTER_OPEN_SEQUENCE, omit(data, registerId))
  }

  /**
   * @private
   * @method _removeRegisterSequenceId
   *
   * @param registerId {String}
   *        The ID of the closed register
   */
  private _removeRegisterSequenceId(registerId: string) {
    const data = getFromStorage<Record<string, string | null>>(
      REGISTER_OPEN_SEQUENCE
    )
    if (!data) {
      return
    }
    data[registerId] = null
    saveToStorage(REGISTER_OPEN_SEQUENCE, data)
  }

  /**
   * Set the time when the register is initialized
   *
   * @method setTimeInitialized
   *
   * @param {Date} time The time when the register is initialized
   */
  public setTimeInitialized(time: Date) {
    this._timeInitialized = time
  }

  /**
   * Set the time when the register is closed
   *
   * @method setTimeClosed
   *
   * @param {Date} time The time when the register is closed
   */
  public setTimeClosed(time: Date) {
    this._timeClosed = time
  }

  /**
   * Track the time taken to close the register
   *
   * @private
   * @method _trackRegisterClosureTime
   */
  private _trackRegisterClosureTime() {
    if (this._timeInitialized && this._timeClosed) {
      const registerClosureTimeInSeconds = Math.round(
        (this._timeClosed.getTime() - this._timeInitialized.getTime()) / 1000
      )
      const user = this.userManager.getLoggedInUser()
      const userRole = user.roles.map(role => role.name)

      trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Register Closure Time', {
        registerClosureTimeInSeconds,
        userRole,
      })
    }
  }
}

const registerClosureManager = new RegisterClosureManager()

/**
 * Responsible for managing the opening and closing of registers.
 *
 * @module registerClosureManager
 */
angular
  .module('webregister.managers')
  .factory('registerClosureManager', () => registerClosureManager)
